import { computed, effect, signal, untracked } from '@preact/signals-react';
import type { ReadonlySignal, Signal } from '@preact/signals-react';
import { Slugs } from '@thinkalpha/common/constants/slugs.js';
import type { Instrument } from '@thinkalpha/common/schemas/instruments.js';
import { inject, injectable } from 'src/features/ioc';
import { instrumentToString } from 'src/lib/instrumentUtils';
import { debouncedSignal } from 'src/lib/signals/debouncedSignal';
import type { TableModel } from 'src/models/TableModel';
import { getPositionsForAccountsAndSymbolsQuery } from 'src/queries/table';
import type {
    PositionInfo,
    PositionInfoGroupService,
    PositionInfoSubscription,
} from 'src/services/PositionInfoGroupService';
import type { ReactBindings } from 'src/types/bindings';

/**
 * Represents a single instrument's subscription within a group.
 * Tracks subscribers and provides the position info signal for this instrument.
 */
class GroupSubscriptionEntry {
    readonly instrumentKey: string;
    readonly positionInfo: Signal<PositionInfo>;
    readonly #subscribers = new Set<string>();
    readonly #onEmpty: () => void;

    constructor(instrumentKey: string, onEmpty: () => void) {
        this.instrumentKey = instrumentKey;
        this.#onEmpty = onEmpty;
        this.positionInfo = signal<PositionInfo>({
            positionSOD: undefined,
            positionBoughtShares: undefined,
            positionSoldShares: undefined,
        });
    }

    addSubscriber(subscriberId: string): void {
        this.#subscribers.add(subscriberId);
    }

    removeSubscriber(subscriberId: string): void {
        this.#subscribers.delete(subscriberId);

        // Notify when no more subscribers
        if (this.#subscribers.size === 0) {
            this.#onEmpty();
        }
    }
}

// Debounce delay in milliseconds for batching instrument key changes
const INSTRUMENT_KEYS_DEBOUNCE_MS = 300;

/**
 * Manages all subscription entries within a group.
 * Coordinates the shared TableModels (pnl + position) and routes position info updates to the correct instruments.
 */
class GroupSubscriptionManager {
    readonly #entries = new Map<string, GroupSubscriptionEntry>();
    readonly #instrumentKeys: Signal<string[]>;
    readonly #debouncedInstrumentKeys: ReadonlySignal<string[]>;
    readonly #debouncedDispose: () => void;
    readonly #pnlTableModel: TableModel;
    readonly #positionTableModel: TableModel;
    readonly #isConnected: ReadonlySignal<boolean>;
    readonly #disposables: DisposableStack;
    readonly #log: ReturnType<ReactBindings['Logger']['getSubLogger']>;
    readonly #accountIds: string[];

    constructor(
        pnlTableModel: TableModel,
        positionTableModel: TableModel,
        accountIds: string[],
        logger: ReturnType<ReactBindings['Logger']['getSubLogger']>,
    ) {
        using stack = new DisposableStack();

        this.#pnlTableModel = pnlTableModel;
        this.#positionTableModel = positionTableModel;
        this.#accountIds = accountIds;
        this.#log = logger;

        // Track instruments in this group
        this.#instrumentKeys = signal<string[]>([]);

        // Create a debounced version of instrument keys to batch rapid changes
        const [debouncedKeys, debouncedDispose] = debouncedSignal(this.#instrumentKeys, INSTRUMENT_KEYS_DEBOUNCE_MS);
        this.#debouncedInstrumentKeys = debouncedKeys;
        this.#debouncedDispose = debouncedDispose;
        stack.defer(() => this.#debouncedDispose());

        // Create computed signals for query options based on debounced instruments
        // We need separate query functions for pnl and position since they return different table data
        const pnlQueryOptionsSignal = computed(() => {
            const baseQuery = getPositionsForAccountsAndSymbolsQuery({
                accountIds: this.#accountIds,
                symbols: this.#debouncedInstrumentKeys.value,
            });

            return {
                ...baseQuery,
                queryFn: async (context: any) => {
                    if (typeof baseQuery.queryFn === 'function') {
                        const result = await baseQuery.queryFn(context);
                        return result.pnl;
                    }
                    throw new Error(
                        `PnL query function is not a function (got ${typeof baseQuery.queryFn}). AccountIds: ${this.#accountIds.join(',')}`,
                    );
                },
            };
        });

        const positionQueryOptionsSignal = computed(() => {
            const baseQuery = getPositionsForAccountsAndSymbolsQuery({
                accountIds: this.#accountIds,
                symbols: this.#debouncedInstrumentKeys.value,
            });

            return {
                ...baseQuery,
                queryFn: async (context: any) => {
                    if (typeof baseQuery.queryFn === 'function') {
                        const result = await baseQuery.queryFn(context);
                        return result.position;
                    }
                    throw new Error(
                        `Position query function is not a function (got ${typeof baseQuery.queryFn}). AccountIds: ${this.#accountIds.join(',')}`,
                    );
                },
            };
        });

        // Initialize pnl table model with signal-based query options
        this.#pnlTableModel.init({
            queryOptions: pnlQueryOptionsSignal as Signal<any>,
            initBounds: { firstRow: 0, windowSize: 100 },
            initThrottle: 1,
            devtoolsName: 'PositionInfo/pnl',
        });

        stack.defer(() => this.#pnlTableModel.destroy());

        // Initialize position table model with signal-based query options
        this.#positionTableModel.init({
            queryOptions: positionQueryOptionsSignal as Signal<any>,
            initBounds: { firstRow: 0, windowSize: 100 },
            initThrottle: 1,
            devtoolsName: 'PositionInfo/position',
        });

        stack.defer(() => this.#positionTableModel.destroy());

        // Connection status is shared across all instruments - both tables must be connected
        this.#isConnected = computed(
            () =>
                this.#pnlTableModel.connectionStatus.value === 'connected' &&
                this.#positionTableModel.connectionStatus.value === 'connected',
        );

        // Set up effect to route pnl updates to the correct instruments
        stack.defer(
            effect(() => {
                if (!this.#isConnected.value) return;

                const pnlUpdateData = this.#pnlTableModel.updateSignal.value?.update;
                if (!pnlUpdateData) return;

                const pnlColumns = this.#pnlTableModel.columnsSignal.value;

                const columnSymbolKey = pnlColumns.find((col) => col.matchSlug === Slugs.positionKey);
                const columnSODPosition = pnlColumns.find((col) => col.matchSlug === Slugs.positionSodPosition);
                const columnBoughtShares = pnlColumns.find((col) => col.matchSlug === Slugs.positionBoughtShares);
                const columnSoldShares = pnlColumns.find((col) => col.matchSlug === Slugs.positionSoldShares);

                if (TRACE) {
                    this.#log.trace('PnL update received', {
                        pnlUpdateData,
                        columnSymbolKey,
                        columnSODPosition,
                        columnBoughtShares,
                        columnSoldShares,
                    });
                }

                if (!columnSymbolKey) {
                    if (TRACE) {
                        this.#log.trace('PnL symbol column not found', { pnlColumns });
                    }
                    return;
                }

                // Extract symbol from the update to route to correct instrument
                const positionKeyValue = pnlUpdateData[columnSymbolKey.id];
                if (typeof positionKeyValue !== 'string') {
                    if (TRACE) {
                        this.#log.trace('PnL position key value is not a string', {
                            positionKeyValue,
                        });
                    }
                    return;
                }

                const [_, symbol] = positionKeyValue.split('-');
                if (!symbol) {
                    if (TRACE) {
                        this.#log.trace('PnL symbol not found after splitting', {
                            positionKeyValue,
                        });
                    }
                    return;
                }

                const entry = this.#entries.get(symbol);
                if (entry) {
                    const currentValue = untracked(() => entry.positionInfo.value);
                    const positionSOD = columnSODPosition
                        ? (pnlUpdateData[columnSODPosition.id] as number | undefined)
                        : undefined;
                    const positionSoldShares = columnSoldShares
                        ? (pnlUpdateData[columnSoldShares.id] as number | undefined)
                        : undefined;
                    const positionBoughtShares = columnBoughtShares
                        ? (pnlUpdateData[columnBoughtShares.id] as number | undefined)
                        : undefined;

                    entry.positionInfo.value = {
                        ...currentValue,
                        positionSOD,
                        positionSoldShares,
                        positionBoughtShares,
                    };

                    if (TRACE) {
                        this.#log.trace('PnL quantity updated', {
                            symbol,
                            positionSOD,
                            positionSoldShares,
                            positionBoughtShares,
                        });
                    }
                }
            }),
        );

        this.#disposables = stack.move();
    }

    /**
     * Get or create an entry for an instrument, and add a subscriber to it.
     * Returns a disposable subscription.
     */
    addSubscriber(instrumentKey: string, subscriberId: string): PositionInfoSubscription {
        // Get or create entry for this instrument
        let entry = this.#entries.get(instrumentKey);

        if (!entry) {
            entry = new GroupSubscriptionEntry(instrumentKey, () => this.#removeInstrument(instrumentKey));
            this.#entries.set(instrumentKey, entry);

            // Update the instrument keys signal (triggers table re-initialization)
            this.#instrumentKeys.value = [...this.#instrumentKeys.value, instrumentKey];

            if (TRACE) {
                this.#log.trace('Added instrument', {
                    instrumentKey,
                    totalInstruments: this.#instrumentKeys.value.length,
                    willDebounce: this.#instrumentKeys.value.length !== this.#debouncedInstrumentKeys.value.length,
                });
            }
        }

        // Add subscriber to the entry
        entry.addSubscriber(subscriberId);

        return {
            pnlTableModel: this.#pnlTableModel,
            positionTableModel: this.#positionTableModel,
            positionInfo: entry.positionInfo,
            isConnected: this.#isConnected,
            [Symbol.dispose]: () => this.#removeSubscriber(instrumentKey, subscriberId),
        };
    }

    #removeSubscriber(instrumentKey: string, subscriberId: string): void {
        const entry = this.#entries.get(instrumentKey);
        if (!entry) return;

        entry.removeSubscriber(subscriberId);
    }

    #removeInstrument(instrumentKey: string): void {
        this.#entries.delete(instrumentKey);

        // Update the instrument keys signal (triggers table re-initialization)
        this.#instrumentKeys.value = this.#instrumentKeys.value.filter((key) => key !== instrumentKey);

        if (TRACE) {
            this.#log.trace('Removed instrument from group', {
                instrumentKey,
                remainingInstruments: this.#instrumentKeys.value.length,
            });
        }
    }

    [Symbol.dispose](): void {
        this.#disposables.dispose();
    }
}

@injectable()
export class PositionInfoGroupServiceImpl implements PositionInfoGroupService {
    readonly #manager: GroupSubscriptionManager;
    readonly #log: ReturnType<ReactBindings['Logger']['getSubLogger']>;

    constructor(
        @inject('TableModel') pnlTableModel: TableModel,
        @inject('TableModel') positionTableModel: TableModel,
        @inject('Logger') logger: ReactBindings['Logger'],
        accountIds: string[],
    ) {
        this.#log = logger.getSubLogger({ name: 'PositionInfoGroupService' }, { component: 'trading' });
        this.#manager = new GroupSubscriptionManager(pnlTableModel, positionTableModel, accountIds, this.#log);

        if (TRACE) {
            this.#log.trace('PositionInfoGroupService initialized', { accountIds });
        }
    }

    subscribe(instrument: Instrument): PositionInfoSubscription {
        const instrumentKey = this.#getInstrumentKey(instrument);
        const subscriberId = crypto.randomUUID();

        if (TRACE) {
            this.#log.trace('Adding subscriber', { instrumentKey, subscriberId });
        }

        return this.#manager.addSubscriber(instrumentKey, subscriberId);
    }

    #getInstrumentKey(instrument: Instrument): string {
        return instrumentToString(instrument);
    }

    [Symbol.dispose](): void {
        if (TRACE) {
            this.#log.trace('Disposing PositionInfoGroupService');
        }

        this.#manager[Symbol.dispose]();
    }
}
